/**
 * YOGA DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated yoga drills organized into skill levels:
 * 1. Beginner Yoga Basics (30 minutes) - Basic yoga poses and breathing
 * 2. Intermediate Yoga Basics (45 minutes) - More yoga poses and sequences
 * 3. Advanced Yoga Basics (45 minutes) - Challenging yoga poses and coordination
 * 4. Beginner Yoga Flow (30 minutes) - Basic yoga flow and movement
 * 5. Intermediate Yoga Flow (45 minutes) - More complex yoga flow
 * 6. Advanced Yoga Flow (45 minutes) - Advanced yoga flow and coordination
 * 7. Beginner Yoga Balance (30 minutes) - Basic balance poses and stability
 * 8. Intermediate Yoga Balance (45 minutes) - More balance poses and coordination
 * 9. Advanced Yoga Balance (45 minutes) - Advanced balance poses and coordination
 * 10. Complete Yoga (60 minutes) - All yoga skills combined
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 2100 (yoga drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'yoga'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Basics', 'Flow', 'Balance')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'None' or 'Yoga mat')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-O (female Studio voice that supports SSML)
 *
 * YOGA DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills)
 * - Equipment limited to optional yoga mat
 * - Appropriate for 6-10 year olds
 * - Focus on safe, basic yoga poses
 * - Progressive skill development within each drill
 * - Emphasis on proper form and breathing
 * - Kid-friendly language (no Sanskrit terms)
 * - Safe movements only
 *
 * CREATED: [Date] - Yoga drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const yogaDrills = [
  // BEGINNER YOGA BASICS (30 minutes) Done
  {
    id: 2100,
    name: 'Beginner Yoga Basics',
    categories: ['Basics', 'beginner'],
    sports: ['yoga'],
    difficulty: 'beginner',
    skill: 'Basics',
    points: 30,
    instructions:
      'Beginner Yoga Basics teaches you simple yoga poses and breathing. This 30-minute session helps you learn basic yoga moves, improve your flexibility, and practice mindful breathing.',
    timedInstructions: [
      // 0:00-3:00 Arrive and breathe
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Mountain definition. Feet hip width, toes point forward, knees over toes, tummy snug, chest calm, shoulders down.',
      },
      {
        time: 24,
        text: 'Inhale arms up. Reach tall. Exhale arms down. Quiet feet.',
      },
      {
        time: 36,
        text: 'Posture check. Two tracks, tall spine, long neck, soft jaw.',
      },
      { time: 48, text: 'Inhale slow for 3, 2, 1. Exhale slow for 3, 2, 1.' },
      {
        time: 60,
        text: 'Shoulders roll. Two forward, two back. Keep breath smooth.',
      },
      { time: 72, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      { time: 84, text: 'Stand in Mountain. Hold for 3, 2, 1. Smile.' },
      {
        time: 96,
        text: 'Reach up. Grow tall like a tree. Exhale arms by sides.',
      },
      {
        time: 108,
        text: 'Reminder. Head over hips, hips over feet. Two tracks.',
      },
      {
        time: 120,
        text: 'Breathe in your nose, breathe out your mouth. Calm and steady.',
      },
      { time: 132, text: 'Stand quiet. Listen to your breath for 3, 2, 1.' },

      // 3:00-6:00 Spine warm up on hands and knees
      {
        time: 144,
        text: 'Come to hands and knees for table. Hands under shoulders, knees under hips, fingers spread.',
      },
      {
        time: 156,
        text: 'Cat pose. Press hands, round your back, tuck chin. Breathe out.',
      },
      {
        time: 168,
        text: 'Cow pose. Lift tail and chest, look forward a little. Breathe in.',
      },
      {
        time: 180,
        text: 'Cat again. Round like a Halloween cat. Slow and gentle.',
      },
      { time: 192, text: 'Cow again. Long belly, shoulders down.' },
      {
        time: 204,
        text: 'Reminder. Wrists straight, elbows soft, knees under hips.',
      },
      { time: 216, text: 'Cat for 3, 2, 1. Cow for 3, 2, 1.' },
      {
        time: 228,
        text: 'Neutral table. Long spine like a table top. Hold for 3, 2, 1.',
      },
      { time: 240, text: 'Tiny hip circles to the right. Small and smooth.' },
      { time: 252, text: 'Tiny hip circles to the left. Breathe steady.' },
      { time: 264, text: 'Posture check. Hands press, long neck, soft face.' },
      {
        time: 276,
        text: 'Sit back to Childs Pose. Hips to heels, arms long, forehead down or on hands.',
      },

      // 6:00-9:00 Downward Dog and Plank
      {
        time: 288,
        text: 'Downward Dog definition. From table, tuck toes, lift hips up and back. Hands press, feet on two tracks.',
      },
      {
        time: 300,
        text: 'Find your Dog. Knees can bend. Spine long. Heels heavy but calm.',
      },
      {
        time: 312,
        text: 'Pedal feet. Bend one knee, then the other. Quiet breath.',
      },
      { time: 324, text: 'Hold Dog for 3, 2, 1. Long arms, long back.' },
      {
        time: 336,
        text: 'Plank definition. Shift forward, shoulders over wrists, body long like a board, tummy snug.',
      },
      {
        time: 348,
        text: 'Step to Plank. Press the floor, heels back, head in line.',
      },
      { time: 360, text: 'Hold Plank for 3, 2, 1. Breathe out slow.' },
      {
        time: 372,
        text: 'Knees down. Hips back to Childs Pose. Rest for 3, 2, 1.',
      },
      { time: 384, text: 'Back to Dog. Hands heavy, hips high, knees soft.' },
      { time: 396, text: 'Forward to Plank. Long body. Calm face.' },
      {
        time: 408,
        text: 'Reminder. Wrists straight, elbows soft, tummy snug.',
      },
      { time: 420, text: 'Childs Pose. Hips to heels, breathe slow.' },

      // 9:00-12:00 Sun flow A lite
      {
        time: 432,
        text: 'Step to the front. Stand in Mountain. Two tracks, tall spine.',
      },
      {
        time: 444,
        text: 'Inhale arms up. Exhale Forward Fold. Bend knees and tip from hips. Hands to legs or blocks.',
      },
      {
        time: 456,
        text: 'Half Lift definition. Hands to shins, back flat like a table, neck long.',
      },
      { time: 468, text: 'Inhale Half Lift. Exhale Fold. Knees soft.' },
      {
        time: 480,
        text: 'Step to Plank. Exhale to knees. Lower belly down slow.',
      },
      {
        time: 492,
        text: 'Cobra definition. Hands by ribs, press little, lift chest small, shoulders down.',
      },
      { time: 504, text: 'Cobra lift. Breathe in. Exhale down. Toes long.' },
      {
        time: 516,
        text: 'Press to table. Tuck toes. Hips high to Downward Dog.',
      },
      {
        time: 528,
        text: 'Look forward. Walk to the front. Fold. Breathe out.',
      },
      { time: 540, text: 'Half Lift. Long back. Fold.' },
      { time: 552, text: 'Rise to Mountain. Arms up, then arms by sides.' },
      { time: 564, text: 'Reminder. Two tracks, knee over toes, tall spine.' },

      // 12:00-15:00 Chair and Forward Fold
      {
        time: 576,
        text: 'Chair definition. Feet hip width, sit back like a tiny chair, knees over toes, chest calm, arms up or hands at heart.',
      },
      {
        time: 588,
        text: 'Sit into Chair. Weight in heels, toes light. Breathe steady.',
      },
      { time: 600, text: 'Hold Chair for 3, 2, 1. Stand tall in Mountain.' },
      {
        time: 612,
        text: 'Chair again. Sit back. Long spine. Look forward a little.',
      },
      {
        time: 624,
        text: 'Fold from Chair. Tip from hips, belly to thighs, knees soft.',
      },
      { time: 636, text: 'Half Lift. Flat back. Fold.' },
      {
        time: 648,
        text: 'Reminder. Knees and toes point the same way. Quiet feet.',
      },
      { time: 660, text: 'Stand tall. Arms up. Arms down. Calm breath.' },
      {
        time: 672,
        text: 'Chair last time. Sit back, breathe out. Hold for 3, 2, 1.',
      },
      { time: 684, text: 'Fold and sway side to side. Neck soft.' },
      { time: 696, text: 'Half Lift. Long back. Fold.' },
      { time: 708, text: 'Step to Dog. Hips high, chest back.' },

      // 15:00-18:00 Low Lunge and Crescent
      {
        time: 720,
        text: 'Step right foot forward for Low Lunge. Back knee down. Front knee over ankle, toes and knee match.',
      },
      {
        time: 732,
        text: 'Lunge definition. Front leg like a door hinge, back leg reaches long, hips square to the front.',
      },
      {
        time: 744,
        text: 'Lift chest. Hands on front thigh or reach up. Breathe in.',
      },
      { time: 756, text: 'Hold Lunge for 3, 2, 1. Shoulders down.' },
      {
        time: 768,
        text: 'Tuck back toes, lift back knee for Crescent. Heel high, tummy snug.',
      },
      { time: 780, text: 'Hold Crescent for 3, 2, 1. Eyes forward.' },
      { time: 792, text: 'Step back to Dog. Hips high. Breathe out.' },
      {
        time: 804,
        text: 'Step left foot forward for Low Lunge. Back knee down. Line up front knee and toes.',
      },
      {
        time: 816,
        text: 'Lift chest. Arms up or hands on thigh. Calm breath.',
      },
      {
        time: 828,
        text: 'Tuck toes, lift to Crescent. Heel high, long spine.',
      },
      { time: 840, text: 'Hold Crescent for 3, 2, 1. Shoulders soft.' },
      { time: 852, text: 'Step back to Dog. Press hands, long back.' },

      // 18:00-21:00 Warriors
      {
        time: 864,
        text: 'Step right foot forward. Spin back heel down for Warrior 1. Front knee bends, back foot turns in, hips face front, arms up.',
      },
      {
        time: 876,
        text: 'Hold Warrior 1 for 3, 2, 1. Knee over ankle, toes and knee match.',
      },
      {
        time: 888,
        text: 'Open to Warrior 2. Hips open, arms reach front and back, gaze forward over front hand.',
      },
      {
        time: 900,
        text: 'Warrior 2 hold. Press outer edge of back foot. Long arms.',
      },
      {
        time: 912,
        text: 'Reverse Warrior. Front palm up, reach up and back a little. Back hand light on leg.',
      },
      {
        time: 924,
        text: 'Return to Warrior 2. Breathe in. Step to Dog. Breathe out.',
      },
      {
        time: 936,
        text: 'Left foot forward, back heel down. Warrior 1. Hips face front, arms up.',
      },
      { time: 948, text: 'Hold Warrior 1 for 3, 2, 1. Tall spine.' },
      { time: 960, text: 'Open to Warrior 2. Arms long. Knee tracks forward.' },
      { time: 972, text: 'Reverse Warrior. Lift and reach. Back hand light.' },
      { time: 984, text: 'Return Warrior 2. Then step to Dog. Calm breath.' },
      {
        time: 996,
        text: 'Reminder. Feet on two tracks, knees over toes, tall spine.',
      },

      // 21:00-24:00 Triangle and Side Angle
      {
        time: 1008,
        text: 'Step right foot forward. Straighten front leg for Triangle set up. Feet wide, front toes forward, back foot turns in.',
      },
      {
        time: 1020,
        text: 'Triangle definition. Tip from hips, front hand to shin or block, top arm to sky, chest open.',
      },
      {
        time: 1032,
        text: 'Hold Triangle for 3, 2, 1. Long sides of your body.',
      },
      {
        time: 1044,
        text: 'Bend front knee for Side Angle. Forearm to thigh, top arm overhead. Reach long.',
      },
      { time: 1056, text: 'Hold Side Angle for 3, 2, 1. Breathe steady.' },
      { time: 1068, text: 'Step back to Dog. Hips high.' },
      {
        time: 1080,
        text: 'Left foot forward. Straighten front leg, Triangle set up.',
      },
      { time: 1092, text: 'Triangle hold. Hand to shin or block. Chest open.' },
      {
        time: 1104,
        text: 'Bend knee for Side Angle. Forearm to thigh, top arm long.',
      },
      { time: 1116, text: 'Hold for 3, 2, 1. Step to Dog. Breathe out.' },
      { time: 1128, text: 'Reminder. Tip from hips, not back. Knees soft.' },
      { time: 1140, text: 'Lower to table. Sit on shins. Breathe calm.' },

      // 24:00-27:00 Balance Tree and Airplane
      {
        time: 1152,
        text: 'Stand in Mountain. Balance time. Pick a spot to look at.',
      },
      {
        time: 1164,
        text: 'Tree definition. Stand on left foot. Place right toes on floor or foot to calf. Hands at heart or up.',
      },
      {
        time: 1176,
        text: 'Grow your Tree. Stand tall. Knee points to the side. Hold for 3, 2, 1.',
      },
      {
        time: 1188,
        text: 'Arms reach like branches. Shoulders down. Breathe slow.',
      },
      { time: 1200, text: 'Step down. Shake legs. Mountain.' },
      {
        time: 1212,
        text: 'Tree on the other side. Stand on right foot. Left foot to toes or calf.',
      },
      {
        time: 1224,
        text: 'Grow your Tree. Tall spine. Calm face. Hold for 3, 2, 1.',
      },
      { time: 1236, text: 'Step down. Mountain. Breathe out.' },
      {
        time: 1248,
        text: 'Airplane definition. Hinge from hips on one leg, back leg lifts, arms wide, spine long.',
      },
      {
        time: 1260,
        text: 'Airplane on left leg. Tip small. Toes point down. Hold for 3, 2, 1.',
      },
      {
        time: 1272,
        text: 'Stand up. Airplane on right leg. Tip small. Calm breath.',
      },
      { time: 1284, text: 'Stand tall. Mountain. Two tracks, soft knees.' },

      // 27:00-30:00 Floor stretches and rest
      {
        time: 1296,
        text: 'Come to sit. Butterfly definition. Feet together, knees wide, hold ankles, sit tall.',
      },
      {
        time: 1308,
        text: 'Butterfly hold for 3, 2, 1. Tip tiny from hips. Chest calm.',
      },
      {
        time: 1320,
        text: 'Seated twist definition. Right hand behind, left hand to knee, sit tall, twist gentle to the right.',
      },
      {
        time: 1332,
        text: 'Twist to right. Long spine. Hold for 3, 2, 1. Breathe out.',
      },
      {
        time: 1344,
        text: 'Twist to left. Sit tall. Shoulders soft. Hold for 3, 2, 1.',
      },
      {
        time: 1356,
        text: 'Bridge definition. Lie down, feet hip width, knees over toes, arms by sides, lift hips small.',
      },
      {
        time: 1368,
        text: 'Lift Bridge. Knees line up with toes, squeeze glutes gentle. Hold for 3, 2, 1. Lower down.',
      },
      {
        time: 1380,
        text: 'Hug knees. Rock tiny side to side. Low back smiles.',
      },
      {
        time: 1392,
        text: 'Happy Baby definition. Knees wide, feet up, hold shins or feet, gentle pull down.',
      },
      {
        time: 1404,
        text: 'Happy Baby. Rock a little. Jaw soft. Hold for 3, 2, 1.',
      },
      {
        time: 1416,
        text: 'Stretch legs long. Point and flex toes. Breathe slow.',
      },
      {
        time: 1428,
        text: 'Savasana definition. Rest on back, arms and legs wide, eyes closed or soft, breathe calm.',
      },
      {
        time: 1440,
        text: 'Savasana. Feel the floor hold you. Breathe in for 3, out for 3.',
      },
      { time: 1452, text: 'Stay in rest. Quiet body, quiet feet, calm face.' },
      {
        time: 1464,
        text: 'Reminder. Let your legs be heavy, belly soft, shoulders melt.',
      },
      {
        time: 1476,
        text: 'Breathe in through nose. Breathe out long through mouth.',
      },
      { time: 1488, text: 'Feel your heart beat calm. Rest for 3, 2, 1.' },
      { time: 1500, text: 'Wiggle fingers and toes. Small moves.' },
      {
        time: 1512,
        text: 'Reach arms overhead. Long like a pencil. Breathe in.',
      },
      { time: 1524, text: 'Hug knees. Rock tiny. Smile.' },
      { time: 1536, text: 'Roll to your side. Pause for 3, 2, 1. Breathe.' },
      {
        time: 1548,
        text: 'Press to sit. Easy seat. Spine tall, shoulders down.',
      },
      { time: 1560, text: 'Hands to heart. Inhale slow. Exhale slow.' },
      {
        time: 1572,
        text: 'Posture scan. Two tracks under sit bones, tall spine, long neck.',
      },
      {
        time: 1584,
        text: 'Thank your body. It worked with focus. Breathe in.',
      },
      { time: 1596, text: 'Breathe out. Soft jaw, soft eyes.' },
      { time: 1608, text: 'Sit tall for 3, 2, 1. Calm mind.' },
      { time: 1620, text: 'Place hands on legs. Feel the breath in and out.' },
      { time: 1632, text: 'Inhale for 3, 2, 1. Exhale for 3, 2, 1.' },
      { time: 1644, text: 'Small smile. You did great.' },
      { time: 1656, text: 'Blink eyes open. Look around gentle.' },
      {
        time: 1668,
        text: 'Stand in Mountain. Two tracks, knees soft, head over hips.',
      },
      {
        time: 1680,
        text: 'Reach arms up. Big inhale. Arms down. Long exhale.',
      },
      { time: 1692, text: 'Hold still for 3, 2, 1. Calm breath.' },
      {
        time: 1704,
        text: 'Coach kudos. Tall spine, quiet feet, steady breath.',
      },
      { time: 1716, text: 'One more inhale. One more exhale.' },
      { time: 1728, text: 'Hands by sides. Stand proud.' },
      { time: 1740, text: 'Ready to finish. Breathe in for 3, 2, 1.' },
      { time: 1752, text: 'Breathe out for 3, 2, 1. Thank you, yogi.' },
      { time: 1764, text: 'Stand quiet. Smile small. Soft eyes.' },
      { time: 1776, text: 'Hold Mountain for 3, 2, 1. Session complete.' },
      { time: 1780, text: 'Beginner Yoga Basics finished. Great work today.' },
    ],
    timeTotal: 1784,
    equipment: 'None',
  },

  // INTERMEDIATE YOGA BASICS (45 minutes) Done
  {
    id: 2101,
    name: 'Intermediate Yoga Basics',
    categories: ['Basics', 'intermediate'],
    sports: ['yoga'],
    difficulty: 'intermediate',
    skill: 'Basics',
    points: 45,
    instructions:
      'Intermediate Yoga Basics builds on your basic yoga skills with harder poses and better coordination. This 45-minute session helps you practice better yoga moves, fun sequences, and cool patterns.',
    timedInstructions: [
      // 0:00-3:00 Arrive, breathe, warm joints
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Mountain definition. Feet hip width, toes point forward, knees over toes, tummy snug, shoulders down, chin level.',
      },
      {
        time: 24,
        text: 'Inhale arms up. Reach tall. Exhale arms down. Quiet feet.',
      },
      {
        time: 36,
        text: 'Posture check. Two tracks, tall spine, long neck, soft jaw.',
      },
      {
        time: 48,
        text: 'Circle ankles. Right two, left two. Knees stay soft.',
      },
      {
        time: 60,
        text: 'Shoulders roll. Two forward, two back. Breathe smooth.',
      },
      {
        time: 72,
        text: 'Neck side. Ear to right shoulder, then to left. Gentle and small.',
      },
      { time: 84, text: 'Stand in Mountain. Hold for 3, 2, 1. Calm breath.' },
      {
        time: 96,
        text: 'Inhale reach up. Exhale Forward Fold. Bend knees and tip from hips.',
      },
      {
        time: 108,
        text: 'Half Lift definition. Hands to shins, back flat like a table, neck long.',
      },
      { time: 120, text: 'Inhale Half Lift. Exhale Fold. Knees soft.' },
      {
        time: 132,
        text: 'Rise to Mountain. Arms up, then arms by sides. Breathe steady.',
      },

      // 3:00-6:00 Table, Cat Cow, Childs Pose
      {
        time: 144,
        text: 'Come to hands and knees for Table. Hands under shoulders, knees under hips, fingers spread.',
      },
      {
        time: 156,
        text: 'Cat pose. Press hands, round your back, tuck chin. Breathe out.',
      },
      {
        time: 168,
        text: 'Cow pose. Lift tail and chest, look forward a little. Breathe in.',
      },
      { time: 180, text: 'Cat again. Round slow and gentle.' },
      { time: 192, text: 'Cow again. Shoulders down, long belly.' },
      {
        time: 204,
        text: 'Reminder. Wrists straight, elbows soft, knees under hips.',
      },
      { time: 216, text: 'Cat for 3, 2, 1. Cow for 3, 2, 1.' },
      {
        time: 228,
        text: 'Neutral Table. Long spine like a table top. Hold for 3, 2, 1.',
      },
      {
        time: 240,
        text: 'Childs Pose definition. Hips to heels, arms long, forehead down or rest on hands.',
      },
      { time: 252, text: 'Childs Pose. Breathe slow. Hold for 3, 2, 1.' },
      { time: 264, text: 'Return to Table. Fingers spread, press the floor.' },
      { time: 276, text: 'Tuck toes. Hips ready to lift.' },

      // 6:00-10:00 Downward Dog, Plank, gentle vinyasa
      {
        time: 288,
        text: 'Downward Dog definition. From Table, tuck toes, lift hips up and back. Hands press, feet on two tracks.',
      },
      {
        time: 300,
        text: 'Find your Dog. Bend knees a little. Lengthen your back. Heels heavy but calm.',
      },
      {
        time: 312,
        text: 'Pedal feet. Bend one knee, then the other. Quiet breath.',
      },
      { time: 324, text: 'Hold Dog for 3, 2, 1. Long arms, long back.' },
      {
        time: 336,
        text: 'Plank definition. Shift forward, shoulders over wrists, body long like a board, tummy snug.',
      },
      {
        time: 348,
        text: 'Step to Plank. Heels reach back, crown reaches forward. Breathe steady.',
      },
      { time: 360, text: 'Knees down. Lower belly to mat slow.' },
      {
        time: 372,
        text: 'Cobra definition. Hands by ribs, press little, lift chest small, shoulders down.',
      },
      { time: 384, text: 'Cobra lift. Inhale up. Exhale down. Toes long.' },
      {
        time: 396,
        text: 'Press to Table. Tuck toes. Hips high to Downward Dog.',
      },
      {
        time: 408,
        text: 'Look forward. Walk to the front. Fold. Breathe out.',
      },
      { time: 420, text: 'Half Lift. Long back. Fold. Rise to Mountain.' },

      // 10:00-14:00 Sun Salutation A - 2 rounds
      {
        time: 432,
        text: 'Sun A. Inhale arms up. Exhale Fold from hips, knees soft.',
      },
      {
        time: 444,
        text: 'Half Lift. Flat back. Exhale step to Plank. Knees down, lower slow.',
      },
      { time: 456, text: 'Cobra small lift. Exhale to Dog. Hips high.' },
      {
        time: 468,
        text: 'Walk to the front. Half Lift. Fold. Rise to Mountain.',
      },
      {
        time: 480,
        text: 'Reminder. Move with breath. Two tracks, tall spine.',
      },
      { time: 492, text: 'Round two. Inhale up. Exhale Fold. Half Lift.' },
      {
        time: 504,
        text: 'Step to Plank. Knees down, lower. Cobra small. Exhale Dog.',
      },
      { time: 516, text: 'Walk to front. Half Lift. Fold. Rise to Mountain.' },
      { time: 528, text: 'Stand and breathe. In for 3, out for 3.' },
      { time: 540, text: 'Shake arms. Soft jaw.' },

      // 14:00-18:00 Chair flow and Forward Fold
      {
        time: 552,
        text: 'Chair definition. Feet hip width, sit back like a tiny chair, knees over toes, chest calm, arms up or hands at heart.',
      },
      {
        time: 564,
        text: 'Sit into Chair. Weight in heels, toes light. Breathe steady.',
      },
      { time: 576, text: 'Hold Chair for 3, 2, 1. Stand tall in Mountain.' },
      {
        time: 588,
        text: 'Chair again. Sit back, long spine. Look forward a little.',
      },
      {
        time: 600,
        text: 'Fold from Chair. Tip from hips, belly to thighs, knees soft.',
      },
      { time: 612, text: 'Half Lift. Flat back. Fold. Step to Dog.' },
      { time: 624, text: 'Dog hold. Press hands. Heels heavy, knees soft.' },
      { time: 636, text: 'Walk forward. Fold. Rise to Mountain. Calm breath.' },

      // 18:00-22:00 Low Lunge to Crescent - right and left
      {
        time: 648,
        text: 'Step right foot forward for Low Lunge. Back knee down. Front knee over ankle, toes and knee match.',
      },
      {
        time: 660,
        text: 'Lift chest. Hands on thigh or reach up. Shoulders down.',
      },
      {
        time: 672,
        text: 'Tuck back toes, lift back knee for Crescent. Heel high, tummy snug.',
      },
      { time: 684, text: 'Hold Crescent for 3, 2, 1. Eyes forward.' },
      { time: 696, text: 'Step back to Dog. Hips high. Breathe out.' },
      {
        time: 708,
        text: 'Step left foot forward for Low Lunge. Back knee down. Front knee lines up with toes.',
      },
      {
        time: 720,
        text: 'Lift chest. Arms up or hands on thigh. Breathe steady.',
      },
      { time: 732, text: 'Lift to Crescent. Heel high, long spine.' },
      { time: 744, text: 'Hold Crescent for 3, 2, 1. Shoulders soft.' },
      { time: 756, text: 'Step back to Dog. Press hands, long back.' },

      // 22:00-26:00 Warriors 1 and 2 with Reverse - both sides
      {
        time: 768,
        text: 'Right side Warrior 1. Step right forward, spin back heel down. Hips face front, arms up.',
      },
      { time: 780, text: 'Warrior 1 hold for 3, 2, 1. Knee over ankle.' },
      {
        time: 792,
        text: 'Open to Warrior 2. Hips open, arms long front and back.',
      },
      {
        time: 804,
        text: 'Warrior 2 hold. Press outer edge of back foot. Gaze forward.',
      },
      {
        time: 816,
        text: 'Reverse Warrior. Front palm up, reach up and back a little. Back hand light.',
      },
      { time: 828, text: 'Return Warrior 2. Step to Dog. Breathe out.' },
      { time: 840, text: 'Left side Warrior 1. Hips face front, arms up.' },
      { time: 852, text: 'Warrior 1 hold for 3, 2, 1. Tall spine.' },
      { time: 864, text: 'Open to Warrior 2. Arms long, knee tracks forward.' },
      { time: 876, text: 'Reverse Warrior. Lift and reach. Calm face.' },
      { time: 888, text: 'Return Warrior 2. Step to Dog. Inhale and exhale.' },
      {
        time: 900,
        text: 'Reminder. Feet on two tracks, knees over toes, tall spine.',
      },

      // 26:00-30:00 Triangle and Side Angle - both sides
      {
        time: 912,
        text: 'Right Triangle set up. Step right forward, straighten front leg, feet wide, back foot turns in.',
      },
      {
        time: 924,
        text: 'Triangle definition. Tip from hips, front hand to shin or block, top arm to sky, chest open.',
      },
      {
        time: 936,
        text: 'Triangle hold for 3, 2, 1. Long sides of your body.',
      },
      {
        time: 948,
        text: 'Side Angle. Bend front knee, forearm to thigh, top arm overhead.',
      },
      { time: 960, text: 'Side Angle hold for 3, 2, 1. Breathe steady.' },
      { time: 972, text: 'Step to Dog. Hips high.' },
      {
        time: 984,
        text: 'Left Triangle set up. Straighten front leg, back foot turns in.',
      },
      { time: 996, text: 'Triangle hold. Hand to shin or block. Chest open.' },
      { time: 1008, text: 'Side Angle. Forearm to thigh, top arm long.' },
      { time: 1020, text: 'Hold for 3, 2, 1. Step to Dog. Breathe out.' },
      { time: 1032, text: 'Lower to Table. Sit on shins. Breathe calm.' },

      // 30:00-33:00 Balance - Tree and Airplane and Warrior 3 prep
      {
        time: 1044,
        text: 'Stand in Mountain. Balance time. Pick a spot to look at.',
      },
      {
        time: 1056,
        text: 'Tree definition. Stand on left foot. Place right toes on floor or foot to calf. Hands at heart or up.',
      },
      {
        time: 1068,
        text: 'Grow your Tree. Stand tall. Knee points to the side. Hold for 3, 2, 1.',
      },
      { time: 1080, text: 'Step down. Shake legs. Mountain.' },
      {
        time: 1092,
        text: 'Tree on the other side. Stand on right foot. Left foot to toes or calf.',
      },
      {
        time: 1104,
        text: 'Grow your Tree. Tall spine. Calm face. Hold for 3, 2, 1.',
      },
      {
        time: 1116,
        text: 'Airplane definition. Hinge from hips on one leg, back leg lifts, arms wide, spine long.',
      },
      {
        time: 1128,
        text: 'Airplane on left leg. Tip small. Toes point down. Hold for 3, 2, 1.',
      },
      {
        time: 1140,
        text: 'Stand up. Airplane on right leg. Tip small. Calm breath.',
      },
      {
        time: 1152,
        text: 'Warrior 3 prep. Hands to hips, hinge a little, back leg long. Hold for 3, 2, 1.',
      },

      // 33:00-36:00 Core and Plank work
      {
        time: 1164,
        text: 'Come to sit. Boat definition. Sit tall, lift chest, lean back a little, lift shins, tummy snug.',
      },
      { time: 1176, text: 'Boat hold for 3, 2, 1. Breathe steady.' },
      { time: 1188, text: 'Boat reset. Feet down. Chest tall.' },
      {
        time: 1200,
        text: 'Boat again. Lift shins. Reach arms. Hold for 3, 2, 1.',
      },
      {
        time: 1212,
        text: 'Cross shins, step to Plank. Body long like a board.',
      },
      {
        time: 1224,
        text: 'Plank hold for 3, 2, 1. Wrists straight, heels back.',
      },
      { time: 1236, text: 'Knees down. Childs Pose. Rest for 3, 2, 1.' },
      { time: 1248, text: 'Downward Dog. Hips high. Breathe smooth.' },

      // 36:00-39:00 Hip openers - Lizard and Pigeon
      {
        time: 1260,
        text: 'Step right foot to outside of right hand for Lizard. Back knee down or lifted. Chest forward.',
      },
      {
        time: 1272,
        text: 'Lizard definition. Front knee over ankle, toes and knee match, hips low, chest long.',
      },
      { time: 1284, text: 'Lizard hold for 3, 2, 1. Shoulders down.' },
      {
        time: 1296,
        text: 'Pigeon definition. Right knee to right wrist area, shin angled, back leg long, hips square to front.',
      },
      {
        time: 1308,
        text: 'Fold in Pigeon or stay tall. Hold for 3, 2, 1. Breathe out slow.',
      },
      { time: 1320, text: 'Step back to Dog. Hips high. Calm face.' },
      {
        time: 1332,
        text: 'Step left foot to outside of left hand for Lizard. Back knee option. Chest forward.',
      },
      {
        time: 1344,
        text: 'Lizard hold. Knee over ankle. Toes and knee match.',
      },
      {
        time: 1356,
        text: 'Left Pigeon. Shin angled, back leg long, hips square.',
      },
      {
        time: 1368,
        text: 'Fold or stay tall. Hold for 3, 2, 1. Breathe steady.',
      },
      { time: 1380, text: 'Step back to Dog. Then lower to sit.' },

      // 39:00-42:00 Seated folds, twist, Bridge
      {
        time: 1392,
        text: 'Butterfly definition. Feet together, knees wide, hold ankles, sit tall.',
      },
      {
        time: 1404,
        text: 'Butterfly hold for 3, 2, 1. Tip tiny from hips. Chest calm.',
      },
      {
        time: 1416,
        text: 'Seated Forward Fold definition. Legs long, toes up, tip from hips, hands to legs or strap.',
      },
      { time: 1428, text: 'Forward Fold hold for 3, 2, 1. Neck soft.' },
      {
        time: 1440,
        text: 'Seated twist definition. Right hand behind, left hand to knee, sit tall, twist gentle to the right.',
      },
      {
        time: 1452,
        text: 'Twist right. Long spine. Hold for 3, 2, 1. Breathe out.',
      },
      {
        time: 1464,
        text: 'Twist left. Sit tall. Shoulders soft. Hold for 3, 2, 1.',
      },
      {
        time: 1476,
        text: 'Bridge definition. Lie down, feet hip width, knees over toes, arms by sides, lift hips small.',
      },
      {
        time: 1488,
        text: 'Lift Bridge. Knees line up with toes, squeeze glutes gentle. Hold for 3, 2, 1. Lower.',
      },
      {
        time: 1500,
        text: 'Hug knees. Rock tiny side to side. Low back smiles.',
      },

      // 42:00-45:00 Savasana and close
      {
        time: 1512,
        text: 'Savasana definition. Rest on back, arms and legs wide, eyes closed or soft, breathe calm.',
      },
      {
        time: 1524,
        text: 'Savasana. Feel the floor hold you. Inhale for 3, exhale for 3.',
      },
      {
        time: 1536,
        text: 'Let your legs be heavy, belly soft, shoulders melt. Quiet feet.',
      },
      { time: 1548, text: 'Feel your heart beat calm. Rest for 3, 2, 1.' },
      { time: 1560, text: 'Wiggle fingers and toes. Small moves.' },
      {
        time: 1572,
        text: 'Reach arms overhead. Long like a pencil. Breathe in.',
      },
      { time: 1584, text: 'Hug knees. Rock tiny. Smile.' },
      { time: 1596, text: 'Roll to your side. Pause for 3, 2, 1. Breathe.' },
      {
        time: 1608,
        text: 'Press to sit. Easy seat. Spine tall, shoulders down.',
      },
      { time: 1620, text: 'Hands to heart. Inhale slow. Exhale slow.' },
      {
        time: 1632,
        text: 'Posture scan. Two tracks under sit bones, tall spine, long neck.',
      },
      {
        time: 1644,
        text: 'Thank your body. It worked with focus. Breathe in.',
      },
      { time: 1656, text: 'Breathe out. Soft jaw, soft eyes.' },
      {
        time: 1668,
        text: 'Stand to Mountain. Feet on two tracks, knees soft, head over hips.',
      },
      {
        time: 1680,
        text: 'Reach arms up. Big inhale. Arms down. Long exhale.',
      },
      { time: 1692, text: 'Hold still for 3, 2, 1. Calm breath.' },

      // Extend calm breath to 45:00
      { time: 1704, text: 'Deep breath in. Long breath out.' },
      { time: 1716, text: 'Shoulders down. Chest calm.' },
      { time: 1728, text: 'Two tracks. Tall spine.' },
      { time: 1740, text: 'Quiet feet. Soft knees.' },
      { time: 1752, text: 'Head over hips. Long neck.' },
      { time: 1764, text: 'Breathe in for 3, 2, 1.' },
      { time: 1776, text: 'Breathe out for 3, 2, 1.' },
      { time: 1788, text: 'Small smile. Calm face.' },
      { time: 1800, text: 'Hands by sides. Stand proud.' },
      { time: 1812, text: 'Inhale slow. Exhale slow.' },
      { time: 1824, text: 'Thank you, yogi. You moved with care.' },
      { time: 1836, text: 'Stand quiet. Breathe steady.' },
      { time: 1848, text: 'Hold Mountain for 3, 2, 1.' },
      { time: 1860, text: 'Soft eyes. Soft jaw.' },
      { time: 1872, text: 'Deep breath in.' },
      { time: 1884, text: 'Long breath out.' },
      { time: 1896, text: 'Posture check. Two tracks, knees over toes.' },
      { time: 1908, text: 'Arms by sides. Calm heart.' },
      { time: 1920, text: 'Breathe in for 3. Breathe out for 3.' },
      { time: 1932, text: 'Quiet room. Quiet body.' },
      { time: 1944, text: 'Stand tall. Smile small.' },
      { time: 1956, text: 'Inhale. Exhale.' },
      { time: 1968, text: 'Feet steady. Head tall.' },
      { time: 1980, text: 'Relax shoulders. Relax hands.' },
      { time: 1992, text: 'Calm breath in. Calm breath out.' },
      { time: 2004, text: 'Hold for 3, 2, 1.' },
      { time: 2016, text: 'You did great today.' },
      { time: 2028, text: 'Thank your breath.' },
      { time: 2040, text: 'Thank your legs and arms.' },
      { time: 2052, text: 'Stand easy. Breathe slow.' },
      { time: 2064, text: 'Soft knees. Tall spine.' },
      { time: 2076, text: 'Head over hips. Calm face.' },
      { time: 2088, text: 'Inhale for 3. Exhale for 3.' },
      { time: 2100, text: 'Hold still for 3, 2, 1.' },
      { time: 2112, text: 'Relax jaw. Relax eyes.' },
      { time: 2124, text: 'Arms by sides. Quiet feet.' },
      { time: 2136, text: 'Deep breath in. Long breath out.' },
      { time: 2148, text: 'Small smile. Kind heart.' },
      { time: 2160, text: 'Stand tall. Stay calm.' },
      { time: 2172, text: 'Inhale. Exhale.' },
      { time: 2184, text: 'Two tracks. Knees soft.' },
      { time: 2196, text: 'Hold still for 3, 2, 1.' },
      { time: 2208, text: 'Breathe in. Breathe out.' },
      { time: 2220, text: 'Shoulders down. Neck long.' },
      { time: 2232, text: 'Calm breath in. Calm breath out.' },
      { time: 2244, text: 'Stand quiet. Soft eyes.' },
      { time: 2256, text: 'In for 3. Out for 3.' },
      { time: 2268, text: 'Hands relaxed. Feet steady.' },
      { time: 2280, text: 'Tall spine. Kind smile.' },
      { time: 2292, text: 'Hold for 3, 2, 1.' },
      { time: 2304, text: 'Deep breath in. Long breath out.' },
      { time: 2316, text: 'Thank your body again.' },
      { time: 2328, text: 'Two tracks. Chin level.' },
      { time: 2340, text: 'Inhale slow. Exhale slow.' },
      { time: 2352, text: 'Quiet feet. Calm mind.' },
      { time: 2364, text: 'Hold still for 3, 2, 1.' },
      { time: 2376, text: 'Stand tall. Feel strong.' },
      { time: 2388, text: 'Breathe in. Breathe out.' },
      { time: 2400, text: 'Relax shoulders. Relax hands.' },
      { time: 2412, text: 'Soft knees. Long neck.' },
      { time: 2424, text: 'Calm breath in. Calm breath out.' },
      { time: 2436, text: 'Hold for 3, 2, 1.' },
      { time: 2448, text: 'Mountain stillness. Stay present.' },
      { time: 2460, text: 'Breathe in. Breathe out.' },
      { time: 2472, text: 'Small smile. Quiet body.' },
      { time: 2484, text: 'Two tracks. Knees over toes.' },
      { time: 2496, text: 'Head over hips. Shoulders down.' },
      { time: 2508, text: 'Inhale. Exhale.' },
      { time: 2520, text: 'Hold still for 3, 2, 1.' },
      { time: 2532, text: 'Calm breath in. Calm breath out.' },
      { time: 2544, text: 'Hands by sides. Feet steady.' },
      { time: 2556, text: 'Tall spine. Soft eyes.' },
      { time: 2568, text: 'Inhale for 3. Exhale for 3.' },
      { time: 2580, text: 'Thank you, yogi.' },
      { time: 2592, text: 'You moved with care.' },
      { time: 2604, text: 'Stand proud. Stay calm.' },
      { time: 2616, text: 'Deep breath in. Long breath out.' },
      { time: 2628, text: 'Two tracks. Quiet feet.' },
      { time: 2640, text: 'Hold for 3, 2, 1.' },
      { time: 2652, text: 'Calm heart. Calm mind.' },
      { time: 2664, text: 'Stand easy. Smile small.' },
      { time: 2676, text: 'Breathe in. Breathe out.' },
      { time: 2688, text: 'Stand tall. Hands by sides.' },
      {
        time: 2690,
        text: 'Namaste. Intermediate Yoga Basics finished. Great work today.',
      },
    ],
    timeTotal: 2694,
    equipment: 'None',
  },
  // ADVANCED YOGA BASICS (45 minutes)
  {
    id: 2102,
    name: 'Advanced Yoga Basics',
    categories: ['Basics', 'advanced'],
    sports: ['yoga'],
    difficulty: 'advanced',
    skill: 'Basics',
    points: 45,
    instructions:
      'Advanced Yoga Basics teaches you fancy yoga moves and tricky coordination. This 45-minute session helps you practice cool yoga moves, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      // 0:00-3:00 Arrive and breathe
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Mountain definition. Feet hip width, toes point forward, knees over toes, tummy snug, shoulders down, chin level.',
      },
      {
        time: 24,
        text: 'Inhale arms up. Reach tall. Exhale arms down. Quiet feet.',
      },
      {
        time: 36,
        text: 'Posture check. Two tracks, tall spine, long neck, soft jaw.',
      },
      {
        time: 48,
        text: 'Wrist circles. Fingers long, circle both ways. Shoulders stay down.',
      },
      { time: 60, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      {
        time: 72,
        text: 'Side body reach. Right hand up, lean left. Then switch.',
      },
      {
        time: 84,
        text: 'Ujjayi breath definition. Breathe in and out through your nose with a soft ocean sound in your throat.',
      },
      { time: 96, text: 'Ujjayi for 3, 2, 1. Calm and steady.' },
      {
        time: 108,
        text: 'Inhale arms up. Exhale Forward Fold. Bend knees and tip from hips.',
      },
      {
        time: 120,
        text: 'Half Lift definition. Hands to shins, back flat like a table, neck long.',
      },
      { time: 132, text: 'Inhale Half Lift. Exhale Fold. Knees soft.' },
      {
        time: 144,
        text: 'Rise to Mountain. Arms up, then arms by sides. Breathe steady.',
      },
      {
        time: 156,
        text: 'Reminder. Head over hips, hips over feet. Two tracks.',
      },
      { time: 168, text: 'Stand quiet. Listen to your breath for 3, 2, 1.' },

      // 3:00-7:00 Sun A with Chaturanga
      { time: 180, text: 'Step to the front. Mountain. Ujjayi on.' },
      { time: 192, text: 'Inhale arms up. Exhale Fold from hips, knees soft.' },
      { time: 204, text: 'Half Lift. Flat back. Exhale step to Plank.' },
      {
        time: 216,
        text: 'Chaturanga definition. From Plank, bend elbows back, shoulders above or higher than elbows. Core tight. Knees can lower.',
      },
      {
        time: 228,
        text: 'Lower slow. Inhale Baby Cobra. Lift chest small, shoulders down. Exhale to Downward Dog.',
      },
      {
        time: 240,
        text: 'Hold Dog for 3, 2, 1. Hands press, hips high, knees soft.',
      },
      {
        time: 252,
        text: 'Walk to the front. Half Lift. Fold. Rise to Mountain.',
      },
      { time: 264, text: 'Round two. Inhale up. Exhale Fold. Half Lift.' },
      { time: 276, text: 'Step to Plank. Strong legs. Option knees down.' },
      {
        time: 288,
        text: 'Chaturanga small. Inhale Cobra or Up Dog. Exhale Dog.',
      },
      {
        time: 300,
        text: 'Look forward. Walk to front. Half Lift. Fold. Rise.',
      },
      {
        time: 312,
        text: 'Reminder. Wrists straight, elbows hug in, tummy snug.',
      },

      // 7:00-11:00 Sun B with Chair and Warrior 1
      {
        time: 324,
        text: 'Chair definition. Feet hip width, sit back like a tiny chair, knees over toes, chest calm, arms up or hands at heart.',
      },
      {
        time: 336,
        text: 'Chair hold for 3, 2, 1. Weight in heels, toes light.',
      },
      {
        time: 348,
        text: 'Fold. Half Lift. Step to Plank. Chaturanga small. Cobra or Up Dog. Downward Dog.',
      },
      {
        time: 360,
        text: 'Right foot steps forward. Warrior 1 definition. Back heel down, toes turn in, hips face front, arms up.',
      },
      { time: 372, text: 'Warrior 1 hold for 3, 2, 1. Front knee over ankle.' },
      {
        time: 384,
        text: 'Hands down. Step back. Chaturanga small. Up Dog or Cobra. Dog.',
      },
      {
        time: 396,
        text: 'Left foot forward. Warrior 1. Hips face front, arms up.',
      },
      { time: 408, text: 'Warrior 1 hold for 3, 2, 1. Tall spine.' },
      {
        time: 420,
        text: 'Hands down. Step back. Chaturanga small. Up Dog. Dog.',
      },
      { time: 432, text: 'Breathe steady. Heels heavy, knees soft.' },

      // 11:00-15:00 Side Plank and Plank work
      {
        time: 444,
        text: 'Plank. Shoulders over wrists, heels back, head in line.',
      },
      {
        time: 456,
        text: 'Side Plank definition. Roll to right edge of foot, stack feet or stagger, right hand down, left hand up. Knees can lower.',
      },
      { time: 468, text: 'Side Plank right for 3, 2, 1. Lift hips.' },
      { time: 480, text: 'Plank reset. Calm breath.' },
      {
        time: 492,
        text: 'Side Plank left. Stack or stagger. Hold for 3, 2, 1.',
      },
      {
        time: 504,
        text: 'Forearm Plank definition. Elbows under shoulders, forearms forward, body long.',
      },
      { time: 516, text: 'Forearm Plank for 3, 2, 1. Tummy snug.' },
      { time: 528, text: 'Knees down. Childs Pose for 3, 2, 1.' },

      // 15:00-19:00 Warrior 2, Reverse, Extended Side Angle
      {
        time: 540,
        text: 'From Dog, step right foot forward. Spin back heel down. Warrior 2 definition. Hips open, arms long front and back.',
      },
      {
        time: 552,
        text: 'Warrior 2 hold for 3, 2, 1. Press outer edge of back foot.',
      },
      {
        time: 564,
        text: 'Reverse Warrior. Front palm up, reach up and back a little. Back hand light.',
      },
      {
        time: 576,
        text: 'Extended Side Angle definition. Front forearm to thigh or hand to block, top arm overhead, chest open.',
      },
      {
        time: 588,
        text: 'Extended Side Angle hold for 3, 2, 1. Long side body.',
      },
      { time: 600, text: 'Hands down. Step to Dog.' },
      { time: 612, text: 'Left side. Warrior 2. Arms long.' },
      { time: 624, text: 'Reverse Warrior. Lift and reach.' },
      {
        time: 636,
        text: 'Extended Side Angle. Forearm to thigh or hand to block.',
      },
      { time: 648, text: 'Step to Dog. Heels heavy. Calm face.' },

      // 19:00-23:00 Warrior 3 and Half Moon
      { time: 660, text: 'Step right foot forward. Crescent. Hands to hips.' },
      {
        time: 672,
        text: 'Warrior 3 definition. Tip from hips, back leg lifts, arms reach forward or hands at heart, hips face down.',
      },
      { time: 684, text: 'Warrior 3 right for 3, 2, 1. Toes point down.' },
      {
        time: 696,
        text: 'Half Moon definition. Hand to block outside front foot, top leg lifts, top arm up, hips open to the side.',
      },
      { time: 708, text: 'Half Moon right for 3, 2, 1. Flex top foot.' },
      { time: 720, text: 'Step back to Crescent. Hands down. Dog.' },
      { time: 732, text: 'Left side. Crescent. Hands to hips.' },
      { time: 744, text: 'Warrior 3 left. Hips face down. Hold for 3, 2, 1.' },
      { time: 756, text: 'Half Moon left. Hips open. Hold for 3, 2, 1.' },
      { time: 768, text: 'Step back to Crescent. Hands down. Dog.' },

      // 23:00-27:00 Twists standing
      {
        time: 780,
        text: 'Right foot forward. Revolved Crescent definition. Back heel high, hands to heart, twist to right, spine long.',
      },
      {
        time: 792,
        text: 'Revolved Crescent right for 3, 2, 1. Knees track forward.',
      },
      {
        time: 804,
        text: 'Chair Twist definition. Feet hip width, sit back, hands to heart, twist to right, spine long.',
      },
      { time: 816, text: 'Step to front. Chair Twist right for 3, 2, 1.' },
      { time: 828, text: 'Chair center. Breathe steady.' },
      { time: 840, text: 'Twist left for 3, 2, 1. Knees even.' },
      {
        time: 852,
        text: 'Step back left. Revolved Crescent left for 3, 2, 1.',
      },
      { time: 864, text: 'Hands down. Step to Dog. Inhale and exhale.' },

      // 27:00-31:00 Arm balance prep
      {
        time: 876,
        text: 'Malasana definition. Squat with feet wide, toes turned a little out, elbows press knees, chest tall.',
      },
      { time: 888, text: 'Malasana hold for 3, 2, 1. Breathe steady.' },
      {
        time: 900,
        text: 'Crow prep definition. Hands shoulder width, knees high on upper arms, lean forward, tummy snug. Toes can stay down.',
      },
      { time: 912, text: 'Crow prep lean for 3, 2, 1. Look forward.' },
      { time: 924, text: 'Rest in Forward Fold. Feet hip width. Neck soft.' },
      {
        time: 936,
        text: 'Dolphin definition. Forearms down, elbows under shoulders, hips lift to a triangle.',
      },
      { time: 948, text: 'Dolphin hold for 3, 2, 1. Press forearms.' },
      { time: 960, text: 'Forearm Plank for 3, 2, 1. Core strong.' },
      { time: 972, text: 'Childs Pose for 3, 2, 1. Breathe calm.' },

      // 31:00-35:00 Hips openers deeper
      {
        time: 984,
        text: 'From Dog, step right foot outside right hand. Lizard. Back knee down or lifted, chest long.',
      },
      { time: 996, text: 'Lizard right for 3, 2, 1. Shoulders down.' },
      {
        time: 1008,
        text: 'Twist option. Right hand reaches up. Hold for 3, 2, 1.',
      },
      {
        time: 1020,
        text: 'Pigeon right definition. Right knee to right wrist area, shin angled, back leg long, hips square to front.',
      },
      {
        time: 1032,
        text: 'Pigeon right. Fold or stay tall. Hold for 3, 2, 1.',
      },
      { time: 1044, text: 'Step back to Dog. Breathe steady.' },
      {
        time: 1056,
        text: 'Lizard left. Front knee over ankle. Toes and knee match.',
      },
      {
        time: 1068,
        text: 'Twist option. Left hand reaches up. Hold for 3, 2, 1.',
      },
      { time: 1080, text: 'Pigeon left. Hips square. Hold for 3, 2, 1.' },
      { time: 1092, text: 'Step back to Dog. Calm face.' },

      // 35:00-39:00 Backbends
      {
        time: 1104,
        text: 'Lower to belly. Locust definition. Arms back, lift chest and legs, tummy on the mat, shoulders down.',
      },
      { time: 1116, text: 'Locust lift for 3, 2, 1. Lower slow.' },
      {
        time: 1128,
        text: 'Bow prep definition. Bend knees, catch ankles, lift chest and legs together, shoulders down.',
      },
      { time: 1140, text: 'Bow prep hold for 3, 2, 1. Lower slow.' },
      {
        time: 1152,
        text: 'Roll to back. Bridge definition. Feet hip width, knees over toes, arms by sides, lift hips.',
      },
      { time: 1164, text: 'Bridge hold for 3, 2, 1. Lower down.' },
      {
        time: 1176,
        text: 'Wheel prep definition. Hands by ears, fingers to shoulders, press to lift a tiny bit or stay in Bridge.',
      },
      { time: 1188, text: 'Wheel prep tiny lift for 3, 2, 1. Then lower.' },
      {
        time: 1200,
        text: 'Hug knees. Rock tiny side to side. Low back smiles.',
      },

      // 39:00-42:00 Hamstrings and twists
      {
        time: 1212,
        text: 'Seated Forward Fold definition. Legs long, toes up, tip from hips, hands to legs or strap.',
      },
      { time: 1224, text: 'Seated Forward Fold hold for 3, 2, 1. Neck soft.' },
      {
        time: 1236,
        text: 'Half Split definition. Right knee down, left leg long, hinge from hips.',
      },
      { time: 1248, text: 'Half Split left hold for 3, 2, 1. Breathe steady.' },
      { time: 1260, text: 'Switch Half Split to right. Hold for 3, 2, 1.' },
      {
        time: 1272,
        text: 'Seated twist definition. Right hand behind, left hand to knee, sit tall, twist gentle to right.',
      },
      { time: 1284, text: 'Twist right hold for 3, 2, 1. Exhale slow.' },
      { time: 1296, text: 'Twist left hold for 3, 2, 1. Shoulders soft.' },

      // 42:00-45:00 Savasana and close
      {
        time: 1308,
        text: 'Savasana definition. Rest on back, arms and legs wide, eyes closed or soft, breathe calm.',
      },
      { time: 1320, text: 'Savasana. Inhale for 3, exhale for 3. Quiet feet.' },
      { time: 1332, text: 'Let legs be heavy, belly soft, shoulders melt.' },
      { time: 1344, text: 'Feel your heart beat calm. Rest for 3, 2, 1.' },
      { time: 1356, text: 'Wiggle fingers and toes. Small moves.' },
      {
        time: 1368,
        text: 'Reach arms overhead. Long like a pencil. Breathe in.',
      },
      { time: 1380, text: 'Hug knees. Rock tiny. Smile.' },
      { time: 1392, text: 'Roll to your side. Pause for 3, 2, 1. Breathe.' },
      {
        time: 1404,
        text: 'Press to sit. Easy seat. Spine tall, shoulders down.',
      },
      { time: 1416, text: 'Hands to heart. Inhale slow. Exhale slow.' },
      {
        time: 1428,
        text: 'Posture scan. Two tracks under sit bones, tall spine, long neck.',
      },
      {
        time: 1440,
        text: 'Thank your body. It worked with focus. Breathe in.',
      },
      { time: 1452, text: 'Breathe out. Soft jaw, soft eyes.' },

      // Extend calm breath to 45:00
      {
        time: 1464,
        text: 'Stand to Mountain. Feet on two tracks, knees soft, head over hips.',
      },
      {
        time: 1476,
        text: 'Reach arms up. Big inhale. Arms down. Long exhale.',
      },
      { time: 1488, text: 'Deep breath in. Long breath out.' },
      { time: 1500, text: 'Shoulders down. Chest calm.' },
      { time: 1512, text: 'Two tracks. Tall spine.' },
      { time: 1524, text: 'Quiet feet. Soft knees.' },
      { time: 1536, text: 'Head over hips. Long neck.' },
      { time: 1548, text: 'Breathe in for 3, 2, 1.' },
      { time: 1560, text: 'Breathe out for 3, 2, 1.' },
      { time: 1572, text: 'Small smile. Calm face.' },
      { time: 1584, text: 'Hands by sides. Stand proud.' },
      { time: 1596, text: 'Inhale slow. Exhale slow.' },
      { time: 1608, text: 'Thank your breath. Stay steady.' },
      { time: 1620, text: 'Stand quiet. Breathe steady.' },
      { time: 1632, text: 'Hold Mountain for 3, 2, 1.' },
      { time: 1644, text: 'Soft eyes. Soft jaw.' },
      { time: 1656, text: 'Deep breath in.' },
      { time: 1668, text: 'Long breath out.' },
      { time: 1680, text: 'Posture check. Two tracks, knees over toes.' },
      { time: 1692, text: 'Arms by sides. Calm heart.' },
      { time: 1704, text: 'Breathe in for 3. Breathe out for 3.' },
      { time: 1716, text: 'Quiet room. Quiet body.' },
      { time: 1728, text: 'Stand tall. Smile small.' },
      { time: 1740, text: 'Inhale. Exhale.' },
      { time: 1752, text: 'Feet steady. Head tall.' },
      { time: 1764, text: 'Relax shoulders. Relax hands.' },
      { time: 1776, text: 'Calm breath in. Calm breath out.' },
      { time: 1788, text: 'Hold for 3, 2, 1.' },
      { time: 1800, text: 'You did great today.' },
      { time: 1812, text: 'Thank your legs and arms.' },
      { time: 1824, text: 'Stand easy. Breathe slow.' },
      { time: 1836, text: 'Soft knees. Tall spine.' },
      { time: 1848, text: 'Head over hips. Calm face.' },
      { time: 1860, text: 'Inhale for 3. Exhale for 3.' },
      { time: 1872, text: 'Hold still for 3, 2, 1.' },
      { time: 1884, text: 'Relax jaw. Relax eyes.' },
      { time: 1896, text: 'Arms by sides. Quiet feet.' },
      { time: 1908, text: 'Deep breath in. Long breath out.' },
      { time: 1920, text: 'Small smile. Kind heart.' },
      { time: 1932, text: 'Stand tall. Stay calm.' },
      { time: 1944, text: 'Inhale. Exhale.' },
      { time: 1956, text: 'Two tracks. Knees soft.' },
      { time: 1968, text: 'Hold still for 3, 2, 1.' },
      { time: 1980, text: 'Breathe in. Breathe out.' },
      { time: 1992, text: 'Shoulders down. Neck long.' },
      { time: 2004, text: 'Calm breath in. Calm breath out.' },
      { time: 2016, text: 'Stand quiet. Soft eyes.' },
      { time: 2028, text: 'In for 3. Out for 3.' },
      { time: 2040, text: 'Hands relaxed. Feet steady.' },
      { time: 2052, text: 'Tall spine. Kind smile.' },
      { time: 2064, text: 'Hold for 3, 2, 1.' },
      { time: 2076, text: 'Deep breath in. Long breath out.' },
      { time: 2088, text: 'Thank your body again.' },
      { time: 2100, text: 'Two tracks. Chin level.' },
      { time: 2112, text: 'Inhale slow. Exhale slow.' },
      { time: 2124, text: 'Quiet feet. Calm mind.' },
      { time: 2136, text: 'Hold still for 3, 2, 1.' },
      { time: 2148, text: 'Stand tall. Feel strong.' },
      { time: 2160, text: 'Breathe in. Breathe out.' },
      { time: 2172, text: 'Relax shoulders. Relax hands.' },
      { time: 2184, text: 'Calm breath in. Calm breath out.' },
      { time: 2196, text: 'Hold for 3, 2, 1.' },
      { time: 2208, text: 'Mountain stillness. Stay present.' },
      { time: 2220, text: 'Breathe in. Breathe out.' },
      { time: 2232, text: 'Small smile. Quiet body.' },
      { time: 2244, text: 'Two tracks. Knees over toes.' },
      { time: 2256, text: 'Head over hips. Shoulders down.' },
      { time: 2268, text: 'Inhale. Exhale.' },
      { time: 2280, text: 'Hold for 3, 2, 1.' },
      { time: 2292, text: 'Calm breath in. Calm breath out.' },
      { time: 2304, text: 'Hands by sides. Feet steady.' },
      { time: 2316, text: 'Tall spine. Soft eyes.' },
      { time: 2328, text: 'Inhale for 3. Exhale for 3.' },
      { time: 2340, text: 'Thank you, yogi.' },
      { time: 2352, text: 'You moved with care.' },
      { time: 2364, text: 'Stand proud. Stay calm.' },
      { time: 2376, text: 'Deep breath in. Long breath out.' },
      { time: 2388, text: 'Two tracks. Quiet feet.' },
      { time: 2400, text: 'Hold for 3, 2, 1.' },
      { time: 2412, text: 'Calm heart. Calm mind.' },
      { time: 2424, text: 'Stand easy. Smile small.' },
      { time: 2436, text: 'Breathe in. Breathe out.' },
      { time: 2448, text: 'Stand tall. Hands by sides.' },
      { time: 2460, text: 'Inhale. Exhale.' },
      { time: 2472, text: 'Quiet feet. Tall spine.' },
      { time: 2484, text: 'Hold for 3, 2, 1.' },
      { time: 2496, text: 'Calm breath in. Calm breath out.' },
      { time: 2508, text: 'Small smile. Kind heart.' },
      { time: 2520, text: 'Stand still. Breathe steady.' },
      { time: 2532, text: 'In for 3. Out for 3.' },
      { time: 2544, text: 'Relax shoulders. Relax hands.' },
      { time: 2556, text: 'Two tracks. Chin level.' },
      { time: 2568, text: 'Hold for 3, 2, 1.' },
      { time: 2580, text: 'Deep breath in. Long breath out.' },
      { time: 2592, text: 'Thank your practice.' },
      { time: 2604, text: 'Stand tall. Quiet feet.' },
      { time: 2616, text: 'Breathe in. Breathe out.' },
      { time: 2628, text: 'Hands by sides. Head over hips.' },
      { time: 2640, text: 'Hold for 3, 2, 1.' },
      { time: 2652, text: 'Calm heart. Calm mind.' },
      { time: 2664, text: 'Stand easy. Smile small.' },
      { time: 2676, text: 'Breathe steady. Stay present.' },
      { time: 2688, text: 'Stand proud. Soft eyes.' },
      {
        time: 2691,
        text: 'Session complete. Advanced Yoga Basics finished. Great work today.',
      },
    ],
    timeTotal: 2694,
    equipment: 'None',
  },
  // BEGINNER YOGA FLOW (30 minutes)

  // INTERMEDIATE YOGA FLOW (45 minutes)

  // ADVANCED YOGA FLOW (45 minutes)

  // BEGINNER YOGA BALANCE (30 minutes)

  // INTERMEDIATE YOGA BALANCE (45 minutes)

  // ADVANCED YOGA BALANCE (45 minutes)

  // COMPLETE YOGA (60 minutes)
];

export default yogaDrills;
